import React, { startTransition, useCallback, useEffect, useState } from 'react'
import Icon from '../components/icons/Icon'
import { useLocation, useNavigate } from 'react-router-dom'
import { usePlaidLink } from 'react-plaid-link'
import type {
  PlaidInstitution,
  LinkedAccountsResponse,
} from '../services/api'
import {
  createPlaidLinkToken,
  disconnectPlaid,
  exchangePlaidPublicToken,
  getLinkedAccounts,
  refreshLinkedAccounts,
} from '../services/api'
import type { PlaidLinkOnSuccess, PlaidLinkOptions } from 'react-plaid-link'
import { useProgressionContext } from '../context/ProgressionContext'
import LinkedInstitutionsSection from '../components/accounts/LinkedInstitutionsSection'
import PlaidLinkPanel from '../components/accounts/PlaidLinkPanel'

type LinkFlow = {
  mode: 'create' | 'update'
  plaidItemId?: number
  institutionName?: string | null
}

type StoredLinkState = { token: string; flow: LinkFlow }

// OAuth-only banks (Chase, Capital One, etc.) bounce the user out to the bank's
// site and redirect back with `?oauth_state_id=...`. To resume Plaid Link we must
// hand it the SAME link token from before the redirect, so we stash it in
// sessionStorage until the OAuth callback hydrates the page again.
const PLAID_OAUTH_STORAGE_KEY = 'vault:plaid-link-state'
const PLAID_OAUTH_QUERY_PARAM = 'oauth_state_id'

const readStoredLinkState = (): StoredLinkState | null => {
  try {
    const raw = window.sessionStorage.getItem(PLAID_OAUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredLinkState>
    if (
      parsed &&
      typeof parsed.token === 'string' &&
      parsed.flow &&
      (parsed.flow.mode === 'create' || parsed.flow.mode === 'update')
    ) {
      return { token: parsed.token, flow: parsed.flow as LinkFlow }
    }
    return null
  } catch (err) {
    console.warn({
      message: 'Failed to read stored Plaid Link state',
      error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
      context: { action: 'readStoredLinkState' },
    })
    return null
  }
}

const writeStoredLinkState = (state: StoredLinkState): void => {
  try {
    window.sessionStorage.setItem(PLAID_OAUTH_STORAGE_KEY, JSON.stringify(state))
  } catch (err) {
    // sessionStorage can be unavailable in private mode; OAuth resumption simply
    // won't work in that case, but the rest of the flow is unaffected.
    console.warn({
      message: 'Failed to persist Plaid Link state for OAuth resumption',
      error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
      context: { action: 'writeStoredLinkState' },
    })
  }
}

const clearStoredLinkState = (): void => {
  try {
    window.sessionStorage.removeItem(PLAID_OAUTH_STORAGE_KEY)
  } catch {
    // ignore — failure to clear is non-critical
  }
}

const isOAuthRedirect = (search: string): boolean =>
  new URLSearchParams(search).has(PLAID_OAUTH_QUERY_PARAM)

/**
 * Accounts page component for linking one or more institutions via Plaid.
 *
 * @returns JSX.Element The accounts page component
 */
const AccountsPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { completeStep } = useProgressionContext()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [linkedData, setLinkedData] = useState<LinkedAccountsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLinking, setIsLinking] = useState(false)
  const [pendingOpen, setPendingOpen] = useState(false)
  const [linkFlow, setLinkFlow] = useState<LinkFlow>({ mode: 'create' })
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isOAuthResumption, setIsOAuthResumption] = useState(false)

  /**
   * Fetch the linked-institution summary from the backend.
   */
  const fetchLinkedAccounts = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getLinkedAccounts()
      startTransition(() => setLinkedData(data))
    } catch (err) {
      console.error({
        message: 'Error fetching linked accounts',
        error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
        context: { action: 'fetchLinkedAccounts' },
      })
      setError('Failed to load linked accounts. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Refresh live balances from Plaid off the initial-render path. Failures are
   * non-blocking: the cached balances already on screen remain visible.
   */
  const refreshLiveBalances = useCallback(async () => {
    try {
      setIsRefreshing(true)
      const data = await refreshLinkedAccounts()
      startTransition(() => setLinkedData(data))
    } catch (err) {
      console.error({
        message: 'Error refreshing live balances',
        error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
        context: { action: 'refreshLiveBalances' },
      })
    } finally {
      setIsRefreshing(false)
    }
  }, [])

  /**
   * Start a Plaid Link flow for either create mode or update mode.
   */
  const initializePlaidLink = useCallback(async (nextFlow: LinkFlow) => {
    try {
      setError(null)
      setIsLinking(true)
      setLinkFlow(nextFlow)
      const token = await createPlaidLinkToken({
        mode: nextFlow.mode,
        plaid_item_id: nextFlow.plaidItemId,
      })
      writeStoredLinkState({ token, flow: nextFlow })
      setLinkToken(token)
      setPendingOpen(true)
    } catch (err) {
      console.error({
        message: 'Error initializing Plaid Link',
        error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
        context: { action: 'initializePlaidLink', flow: nextFlow.mode, plaidItemId: nextFlow.plaidItemId },
      })
      setPendingOpen(false)
      setIsLinking(false)
      setError('Failed to initialize bank connection. Please try again.')
    }
  }, [])

  useEffect(() => {
    void (async () => {
      await fetchLinkedAccounts()
      void refreshLiveBalances()
    })()
  }, [fetchLinkedAccounts, refreshLiveBalances])

  // Resume Plaid Link after an OAuth bank redirect. The Link instance needs the
  // ORIGINAL link token plus `receivedRedirectUri`; without resumption, users
  // who pick OAuth-only banks (Chase, Capital One, Wells Fargo, etc.) would
  // land back on /accounts and the flow would silently stall.
  useEffect(() => {
    if (!isOAuthRedirect(location.search)) {
      return
    }

    const stored = readStoredLinkState()
    if (!stored) {
      clearStoredLinkState()
      setError(
        'Your bank linking session expired. Please start linking your account again.'
      )
      navigate('/accounts', { replace: true })
      return
    }

    setIsOAuthResumption(true)
    setLinkFlow(stored.flow)
    setLinkToken(stored.token)
    setIsLinking(true)
    setPendingOpen(true)
  }, [location.search, navigate])

  const dismissSuccessLater = () => {
    window.setTimeout(() => setSuccessMessage(null), 5000)
  }

  // Drop the `oauth_state_id` query param after Link finishes so a refresh
  // doesn't try to resume a session that's already been consumed.
  const cleanUpOAuthQuery = useCallback(() => {
    if (isOAuthRedirect(location.search)) {
      navigate('/accounts', { replace: true })
    }
    setIsOAuthResumption(false)
  }, [location.search, navigate])

  /**
   * Poll the linked-accounts endpoint until the institution appears or we
   * time out. Used as a fallback when Plaid's in-browser onSuccess delivers
   * an empty public_token; the server-side SESSION_FINISHED webhook should
   * persist the new institution shortly after the Link session closes.
   */
  const waitForWebhookLinkedInstitution = useCallback(
    async (institutionName?: string | null, institutionId?: string | null): Promise<boolean> => {
      const matchesNewInstitution = (data: LinkedAccountsResponse | null): boolean => {
        if (!data?.institutions?.length) return false
        return data.institutions.some((inst) => {
          if (institutionId && inst.institution_id) {
            return inst.institution_id === institutionId
          }
          if (institutionName && inst.institution_name) {
            return inst.institution_name.toLowerCase() === institutionName.toLowerCase()
          }
          return false
        })
      }

      // Up to ~20 seconds of polling: webhook latency from Plaid is usually a
      // few seconds, but we leave headroom for cold starts.
      const deadline = Date.now() + 20_000
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        try {
          const data = await getLinkedAccounts()
          startTransition(() => setLinkedData(data))
          if (matchesNewInstitution(data)) return true
        } catch (pollErr) {
          console.warn({
            message: 'Polling linked accounts after webhook failed',
            error:
              pollErr instanceof Error
                ? { message: pollErr.message, stack: pollErr.stack }
                : { message: String(pollErr) },
            context: { action: 'waitForWebhookLinkedInstitution' },
          })
        }
      }
      return false
    },
    []
  )

  /**
   * Handles successful Plaid Link completion.
   */
  const onPlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        setError(null)

        console.info({
          message: 'Plaid Link success',
          context: {
            mode: linkFlow.mode,
            institution: metadata.institution?.name,
            accounts: metadata.accounts?.length,
            plaidItemId: linkFlow.plaidItemId,
            hasPublicToken: Boolean(publicToken),
          },
        })

        if (linkFlow.mode === 'update') {
          setSuccessMessage(
            `Successfully reconnected ${linkFlow.institutionName || metadata.institution?.name || 'your institution'}.`
          )
          await fetchLinkedAccounts()
        } else if (publicToken) {
          await exchangePlaidPublicToken(publicToken, {
            institution_name: metadata.institution?.name,
            institution_id: metadata.institution?.institution_id,
          })
          setSuccessMessage(
            `Successfully connected ${metadata.institution?.name || 'your bank account'}!`
          )
          await fetchLinkedAccounts()
        } else {
          // Empty public_token is most common with OAuth banks (e.g., Robinhood)
          // where the iframe closes before forwarding the token. The server's
          // SESSION_FINISHED webhook handler will exchange it for us; just poll
          // for the institution to show up.
          setSuccessMessage(
            `Finishing connection for ${metadata.institution?.name || 'your bank'}…`
          )
          const arrived = await waitForWebhookLinkedInstitution(
            metadata.institution?.name,
            metadata.institution?.institution_id
          )
          if (arrived) {
            setSuccessMessage(
              `Successfully connected ${metadata.institution?.name || 'your bank account'}!`
            )
          } else {
            setSuccessMessage(null)
            setError(
              'Your bank link is still finalizing. Refresh in a minute, or try again if it does not appear.'
            )
          }
        }

        dismissSuccessLater()
        void refreshLiveBalances()
      } catch (err) {
        console.error({
          message: 'Error completing bank connection',
          error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
          context: { action: 'onPlaidSuccess', flow: linkFlow.mode, plaidItemId: linkFlow.plaidItemId },
        })
        setError('Failed to complete bank connection. Please try again.')
      } finally {
        clearStoredLinkState()
        setLinkToken(null)
        setPendingOpen(false)
        setIsLinking(false)
        setLinkFlow({ mode: 'create' })
        cleanUpOAuthQuery()
      }
    },
    [fetchLinkedAccounts, refreshLiveBalances, linkFlow, cleanUpOAuthQuery, waitForWebhookLinkedInstitution]
  )

  /**
   * Plaid Link configuration.
   */
  const plaidConfig: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err, metadata) => {
      if (err) {
        const requiresOAuth =
          err.error_code === 'REQUIRES_OAUTH' || metadata.status === 'requires_oauth'
        console.warn({
          message: 'Plaid Link exited with error',
          error: err.display_message || err.error_message,
          context: { errorCode: err.error_code, flow: linkFlow.mode, plaidItemId: linkFlow.plaidItemId },
        })
        setError(
          requiresOAuth
            ? 'This institution requires OAuth. Continue through your bank, then return to this page to finish linking.'
            : err.display_message || err.error_message || 'Plaid Link exited unexpectedly.'
        )
      }
      console.info({
        message: 'Plaid Link closed',
        context: { status: metadata.status, flow: linkFlow.mode, plaidItemId: linkFlow.plaidItemId },
      })
      clearStoredLinkState()
      setPendingOpen(false)
      setIsLinking(false)
      cleanUpOAuthQuery()
    },
    // Use the React Router location plus the runtime origin so OAuth resumption
    // works deterministically across SSR/test contexts where window.location.href
    // may not reflect the active route URL.
    ...(isOAuthResumption
      ? { receivedRedirectUri: `${window.location.origin}${location.pathname}${location.search}` }
      : {}),
  }

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink(plaidConfig)

  useEffect(() => {
    if (pendingOpen && plaidReady) {
      openPlaidLink()
      setPendingOpen(false)
    }
  }, [openPlaidLink, pendingOpen, plaidReady])

  /**
   * Start a new institution link flow.
   */
  const handleLinkAccount = () => {
    void initializePlaidLink({ mode: 'create' })
  }

  /**
   * Start a reconnect flow for a specific institution.
   */
  const handleReconnect = (institution: PlaidInstitution) => {
    void initializePlaidLink({
      mode: 'update',
      plaidItemId: institution.plaid_item_id,
      institutionName: institution.institution_name,
    })
  }

  /**
   * Disconnect a specific linked institution.
   */
  const handleDisconnect = async (institution: PlaidInstitution) => {
    const institutionLabel = institution.institution_name || 'this institution'
    if (!window.confirm(`Disconnect ${institutionLabel}? This will remove its linked account access.`)) {
      return
    }

    try {
      setIsLoading(true)
      await disconnectPlaid(institution.plaid_item_id)
      setSuccessMessage(`${institutionLabel} disconnected successfully.`)
      await fetchLinkedAccounts()
      dismissSuccessLater()
    } catch (err) {
      console.error({
        message: 'Error disconnecting account',
        error: err instanceof Error ? { message: err.message, stack: err.stack } : { message: String(err) },
        context: { action: 'handleDisconnect', plaidItemId: institution.plaid_item_id },
      })
      setError('Failed to disconnect account. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const institutions = linkedData?.institutions ?? []
  const hasLinkedInstitutions = institutions.length > 0

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <h1>Link Your Financial Institutions</h1>
        <p>Connect multiple banks or brokerages to power a more complete financial analysis.</p>
      </div>

      {error && (
        <div className="error-banner">
          <Icon name="alert" className="error-icon" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          <Icon name="check" className="success-icon" />
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)} className="close-success">×</button>
        </div>
      )}

      <div className="accounts-container">
        <PlaidLinkPanel
          hasLinkedInstitutions={hasLinkedInstitutions}
          isLinking={isLinking}
          isLoading={isLoading}
          plaidReady={plaidReady}
          linkToken={linkToken}
          error={error}
          onLinkAccount={handleLinkAccount}
        />

        {isLoading && !linkedData && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your linked institutions...</p>
          </div>
        )}

        {linkedData?.partial_errors.length ? (
          <div className="partial-error-panel">
            <h3>Some institutions need attention</h3>
            {linkedData.partial_errors.map((partialError) => (
              <p key={partialError.plaid_item_id}>
                <strong>{partialError.institution_name || 'Linked institution'}:</strong>{' '}
                {partialError.message || 'Unable to refresh this institution right now.'}
              </p>
            ))}
          </div>
        ) : null}

        {hasLinkedInstitutions && (
          <LinkedInstitutionsSection
            linkedData={linkedData}
            institutions={institutions}
            isRefreshing={isRefreshing}
            isLinking={isLinking}
            isLoading={isLoading}
            onReconnect={handleReconnect}
            onDisconnect={handleDisconnect}
          />
        )}

        {!isLoading && !hasLinkedInstitutions && (
          <div className="empty-state">
            <span className="empty-state-icon"><Icon name="vault" /></span>
            <h3>No Institutions Linked Yet</h3>
            <p>Click the button above to securely connect your first bank or brokerage account.</p>
            <div className="security-badges">
              <span className="badge"><Icon name="lock" />Bank-level encryption</span>
              <span className="badge"><Icon name="check" />Read-only access</span>
              <span className="badge"><Icon name="shield" />Powered by Plaid</span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem' }}>
          <button
            className="btn btn-primary"
            onClick={() => {
              completeStep(1)
              navigate('/profile')
            }}
          >
            {hasLinkedInstitutions ? 'Continue to Profile' : 'Skip & Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AccountsPage
